import { ComponentType, Field, IForm, ISearchForm } from '@common/types';
import React, { FC, Fragment } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import CusInput from '@common/components/Input';
import CusLabel from '@common/components/Label';
import CusPassword from '@common/components/Password';
import CusSelect from '@common/components/Select';
import CusDatePicker from '@common/components/DatePicker';
import CusCheckbox from '@common/components/Checkbox';
import CusImageUpload from '@common/components/ImageUpload';
import CusFileUpload from '@common/components/FileUpload';
import CustMultipleCheckbox from '@common/components/MultipleCheckbox';
import CustMultipleRadiobox from '@common/components/MultipleRadiobox';
import CusWebView from '@common/components/WebView';
import { dataTypes } from '@common/constants';
import BindingComponent from '@common/screens/RenderScreen/BindingComponent';
import { uuidv4 } from '@common/utils/record';
import { RelationshipFieldType } from 'click-types';

type Props = {
  object: ISearchForm;
  field: Field;
  active: Boolean;
  hasError: any;
};

const FormField = (props: Props) => {
  let { field, object, hasError } = props;

  const getLabelProps = () => {
    const { fieldStyles, style } = object || {};
    const labelStyles = (fieldStyles && fieldStyles.labels) || {};
    let { fontFamily } = labelStyles;

    // supply the normalized body font family just incase the field styles
    // do not have a fontFamily property
    // fontFamily = fontFamily || normalizeFontFamily('@body', branding);
    const id = uuidv4();

    return {
      id,
      type: ComponentType.label,
      fontSize: style?.fontSize || 14,
      fontFamily,
      color: style?.color || labelStyles.color || '#000',
      text: field.label,
      ...labelStyles,
      multiline: true,
      maxWidth: object.width - 38,
      lineHeight: 27,
    };
  };

  const getInputStyles = () => {
    const { fieldStyles, style } = object || {};

    return {
      ...(fieldStyles && fieldStyles.inputs),
      // Apply style object properties with priority
      ...(style?.color && { color: style.color }),
      ...(style?.fontSize && { fontSize: style.fontSize }),
    };
  };

  const getInputProps = (isSecondInput = false, inputWidth?: number) => {
    let fieldType = field.type as string | Record<string, any>;
    let layerType = ComponentType.input;
    let inputStyles = getInputStyles();
    let fontSize = inputStyles.fontSize || 16;
    let padding = inputStyles.padding === undefined ? 10 : inputStyles.padding;
    let numberOfLines =
      fieldType === 'number' ? false : field?.numberOfLines || false;
    let multiline = field.multiline || inputStyles.multiline || numberOfLines;
    let inputType = 'default';
    let height = fontSize * 1.3 + 2 * padding;

    // Fixed ID generation for range inputs
    let id = `${object.id}-${field?.fid || field.fieldId}`;
    if (field.range) {
      id = isSecondInput ? `${id}-to` : `${id}-from`;
    }

    let database;

    let otherProps: any = {};
    if (fieldType === dataTypes.IMAGE) {
      layerType = ComponentType.imageUpload;
      inputStyles.color = inputStyles.accentColor;
      inputStyles.imageUploadType = 'fullWidth';
      let { width } = object;
      height = 150;
    } else if (fieldType === dataTypes.TEXT) {
      if (fieldType === dataTypes.EMAIL) inputType = dataTypes.EMAIL;
    } else if (
      fieldType === dataTypes.DATE ||
      fieldType === dataTypes.DATE_ONLY
    ) {
      layerType = ComponentType.datePicker;
      inputStyles.defaultDateFormat = fieldType;
    } else if (fieldType === dataTypes.FILE) {
      layerType = ComponentType.fileUpload;
      inputStyles.color = inputStyles.accentColor || '#6200f3';
      height = 42;
    } else if (fieldType === dataTypes.NUMBER) {
      inputType = 'number';
      if (field.range) {
        layerType = ComponentType.input;
      }
    } else if (fieldType === dataTypes.PASSWORD) {
      inputType = 'password';
    } else if (fieldType === dataTypes.BOOLEAN) {
      layerType = ComponentType.checkbox;
    } else if (typeof fieldType === 'object') {
      if (fieldType?.type === RelationshipFieldType.MANY_TO_ONE) {
        layerType = ComponentType.select;
        if (field.binding) {
          database = {
            ...field.binding,
            bindingType: 'list',
          };
        }
      }
      if (fieldType?.type === RelationshipFieldType.MANY_TO_MANY) {
        layerType = ComponentType.multiCheckbox;
        otherProps.label = `Choose multiple`;
        otherProps.shapeType = 'checkbox';
        if (field.binding) {
          database = {
            ...field.binding,
            bindingType: 'list',
          };
        }
      }
      if (fieldType?.type === RelationshipFieldType.ONE_TO_MANY) {
        if (field.dropDown) {
          layerType = ComponentType.select;
          if (field.binding) {
            database = {
              ...field.binding,
              bindingType: 'list',
            };
          }
        } else {
          layerType = ComponentType.multiRadiobox;
          otherProps.label = `Select an Option`;
        }
      }
    }

    if (multiline) {
      height =
        fieldType === dataTypes.TEXT && multiline
          ? 1.3 * 2 * fontSize + 2 * padding
          : 80;
    }

    const errorColor = inputStyles.errorColor || 'red';

    // Handle range placeholder
    let placeholder = field.placeholder;
    if (
      field.range &&
      (fieldType === dataTypes.NUMBER ||
        fieldType === dataTypes.DATE ||
        fieldType === dataTypes.DATE_ONLY)
    ) {
      if (fieldType === dataTypes.NUMBER) {
        placeholder = isSecondInput
          ? `Max ${field.placeholder || 'value'}`.trim()
          : `Min ${field.placeholder || 'value'}`.trim();
      } else {
        placeholder = isSecondInput
          ? `End ${field.placeholder || 'date'}`.trim()
          : `Start ${field.placeholder || 'date'}`.trim();
      }
    }

    return {
      id,
      type: layerType,
      field,
      database,
      marginTop: 7,
      borderWidth: 1,
      ...inputStyles,
      fontSize,
      multiline,
      inputType,
      borderColor: hasError ? errorColor : inputStyles.borderColor || '#ddd',
      borderRadius: inputStyles.borderRadius || 4,
      placeholder,
      padding: inputStyles.padding || 10,
      height,
      width: inputWidth, // Add width support for range inputs
      backgroundColor: '#fff',
      screenUuid: object.screenUuid,
      formId: object.id,
      isFormItem: true,
      ...otherProps,
    };
  };

  const getCheckboxProps = () => {
    let { fieldStyles, submitButton } = object;

    fieldStyles = fieldStyles || { inputs: {} };
    submitButton = submitButton || {};

    let borderColor = fieldStyles.inputs.borderColor || '#ccc';
    let backgroundColor = submitButton.backgroundColor || '#6200ee';

    return {
      id: `${object.id}-${field?.fid || field.fieldId}`,
      type: ComponentType.checkbox,
      activeColor: backgroundColor,
      inactiveColor: borderColor,
      marginRight: 10,
    };
  };

  const renderCheckbox = () => {
    const mappingLabel: { [key: string]: any } = {
      [ComponentType.label]: CusLabel,
    };

    const mappingCheckbox: { [key: string]: any } = {
      [ComponentType.checkbox]: CusCheckbox,
    };

    const checkboxProps = getCheckboxProps();
    const labelProps = {
      ...getLabelProps(),
      // marginLeft: 5,
    };

    let CheckboxObject = mappingCheckbox[checkboxProps.type] as FC;
    let LabelObject = mappingLabel[labelProps.type] as FC;

    return (
      <View style={[styles.formRow]}>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <BindingComponent ObjectClass={CheckboxObject} obj={checkboxProps} />
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              minHeight: 27,
              marginLeft: 5,
            }}
            nativeID="form-label-wrapper"
          >
            <BindingComponent ObjectClass={LabelObject} obj={labelProps} />
          </View>
          {field?.required && (
            <View>
              <Text style={styles.startRequired}>*</Text>
            </View>
          )}
        </View>
        {hasError ? renderErrorMessage() : null}
      </View>
    );
  };

  const renderErrorMessage = () => {
    const { hasError } = props;
    const { message } = hasError;
    let inputStyles = getInputStyles();

    const errorStyles = {
      color: inputStyles.errorColor,
    };

    if (!hasError) return null;
    if (!message) return null;
    return <Text style={[styles.errorMessage, errorStyles]}>{message}</Text>;
  };

  const renderRangeInputs = () => {
    const mappingInput: { [key: string]: any } = {
      [ComponentType.input]: CusInput,
      [ComponentType.password]: CusPassword,
      [ComponentType.select]: CusSelect,
      [ComponentType.datePicker]: CusDatePicker,
      [ComponentType.imageUpload]: CusImageUpload,
      [ComponentType.fileUpload]: CusFileUpload,
      [ComponentType.webView]: CusWebView,
      [ComponentType.multiCheckbox]: CustMultipleCheckbox,
      [ComponentType.multiRadiobox]: CustMultipleRadiobox,
    };

    const mappingLabel: { [key: string]: any } = {
      [ComponentType.label]: CusLabel,
    };

    // Calculate input width (leaving space for dash)
    const totalWidth = object.width || 300;
    const inputWidth = totalWidth / 2 - 20; // More space for dash

    const firstInputProps = getInputProps(false, inputWidth);
    const secondInputProps = getInputProps(true, inputWidth);
    const labelProps = getLabelProps();

    let FirstInputObject = mappingInput[firstInputProps.type] as FC;
    let SecondInputObject = mappingInput[secondInputProps.type] as FC;
    let LabelObject = mappingLabel[labelProps.type] as FC;

    return (
      <View style={[styles.formRow]}>
        <View style={styles.fieldLabel} nativeID="click-form-field-label">
          <BindingComponent ObjectClass={LabelObject} obj={labelProps} />
          {field?.required && (
            <View>
              <Text style={styles.startRequired}>*</Text>
            </View>
          )}
        </View>
        <View style={styles.rangeContainer}>
          <View style={styles.rangeInputContainer}>
            <BindingComponent
              ObjectClass={FirstInputObject}
              obj={firstInputProps}
            />
          </View>
          <View style={styles.dashContainer}>
            <Text style={styles.dashText}>~</Text>
          </View>
          <View style={styles.rangeInputContainer}>
            <BindingComponent
              ObjectClass={SecondInputObject}
              obj={secondInputProps}
            />
          </View>
        </View>
        {hasError ? renderErrorMessage() : null}
      </View>
    );
  };

  if (field.type === dataTypes.BOOLEAN) {
    return renderCheckbox();
  }

  // Check if this should render range inputs
  const shouldRenderRange =
    field.range &&
    (field.type === dataTypes.NUMBER ||
      field.type === dataTypes.DATE ||
      field.type === dataTypes.DATE_ONLY);

  if (shouldRenderRange) {
    return <Fragment>{renderRangeInputs()}</Fragment>;
  }

  const mappingInput: { [key: string]: any } = {
    [ComponentType.input]: CusInput,
    [ComponentType.password]: CusPassword,
    [ComponentType.select]: CusSelect,
    [ComponentType.datePicker]: CusDatePicker,
    [ComponentType.imageUpload]: CusImageUpload,
    [ComponentType.fileUpload]: CusFileUpload,
    [ComponentType.webView]: CusWebView,
    [ComponentType.multiCheckbox]: CustMultipleCheckbox,
    [ComponentType.multiRadiobox]: CustMultipleRadiobox,
  };

  const mappingLabel: { [key: string]: any } = {
    [ComponentType.label]: CusLabel,
  };

  const inputProps = getInputProps();
  const labelProps = getLabelProps();

  let InputObject = mappingInput[inputProps.type] as FC;
  let LabelObject = mappingLabel[labelProps.type] as FC;

  const renderFormField = () => {
    try {
      return (
        <View style={[styles.formRow]}>
          <View style={styles.fieldLabel} nativeID="click-form-field-label">
            <BindingComponent ObjectClass={LabelObject} obj={labelProps} />
            {field?.required && (
              <View>
                <Text style={styles.startRequired}>*</Text>
              </View>
            )}
          </View>
          <BindingComponent ObjectClass={InputObject} obj={inputProps} />
          {hasError ? renderErrorMessage() : null}
        </View>
      );
    } catch (err) {
      console.log('err', err);
    }
  };

  return <Fragment>{renderFormField()}</Fragment>;
};

export default FormField;

const styles = StyleSheet.create({
  formRow: {
    marginBottom: 18,
  },
  errorMessage: {
    marginTop: 6,
  },
  startRequired: { color: 'red', marginLeft: 3, fontSize: 17 },
  fieldLabel: { display: 'flex', flexDirection: 'row', alignItems: 'center' },
  rangeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rangeInputContainer: {
    flex: 1,
  },
  dashContainer: {
    paddingHorizontal: 15,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dashText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
});
